import type React from "react";
import { cn } from "@src/lib/utils";

export type FlexDirection = "row" | "row-reverse" | "column" | "column-reverse";
export type AlignItems =
  | "flex-start"
  | "flex-end"
  | "center"
  | "baseline"
  | "stretch";
export type JustifyContent =
  | "flex-start"
  | "flex-end"
  | "center"
  | "space-between"
  | "space-around"
  | "space-evenly";
export type FlexWrap = "wrap" | "nowrap" | "wrap-reverse";
export type AlignContent =
  | "flex-start"
  | "flex-end"
  | "center"
  | "space-between"
  | "space-around"
  | "space-evenly"
  | "stretch";
export type AlignSelf =
  | "auto"
  | "flex-start"
  | "flex-end"
  | "center"
  | "baseline"
  | "stretch";
export type JustifySelf =
  | "auto"
  | "flex-start"
  | "flex-end"
  | "center"
  | "baseline"
  | "stretch";
export type FlexboxGap = "none" | "xs" | "sm" | "md" | "lg" | "xl";

type FlexboxOwnProps = {
  flexDirection?: FlexDirection;
  alignItems?: AlignItems;
  justifyContent?: JustifyContent;
  flexWrap?: FlexWrap;
  alignContent?: AlignContent;
  alignSelf?: AlignSelf;
  justifySelf?: JustifySelf;
  align?: AlignItems;
  justify?: JustifyContent;
  gap?: FlexboxGap;
};

export type FlexboxProps<T extends React.ElementType = "div"> = {
  as?: T;
} & FlexboxOwnProps &
  Omit<React.ComponentPropsWithoutRef<T>, keyof FlexboxOwnProps | "as">;

const FLEX_DIRECTION_CLASSES: Record<FlexDirection, string> = {
  row: "flex-row",
  "row-reverse": "flex-row-reverse",
  column: "flex-col",
  "column-reverse": "flex-col-reverse",
};

const ALIGN_ITEMS_CLASSES: Record<AlignItems, string> = {
  "flex-start": "items-start",
  "flex-end": "items-end",
  center: "items-center",
  baseline: "items-baseline",
  stretch: "items-stretch",
};

const JUSTIFY_CONTENT_CLASSES: Record<JustifyContent, string> = {
  "flex-start": "justify-start",
  "flex-end": "justify-end",
  center: "justify-center",
  "space-between": "justify-between",
  "space-around": "justify-around",
  "space-evenly": "justify-evenly",
};

const FLEX_WRAP_CLASSES: Record<FlexWrap, string> = {
  wrap: "flex-wrap",
  nowrap: "flex-nowrap",
  "wrap-reverse": "flex-wrap-reverse",
};

const ALIGN_CONTENT_CLASSES: Record<AlignContent, string> = {
  "flex-start": "content-start",
  "flex-end": "content-end",
  center: "content-center",
  "space-between": "content-between",
  "space-around": "content-around",
  "space-evenly": "content-evenly",
  stretch: "content-stretch",
};

const ALIGN_SELF_CLASSES: Record<AlignSelf, string> = {
  auto: "self-auto",
  "flex-start": "self-start",
  "flex-end": "self-end",
  center: "self-center",
  baseline: "self-baseline",
  stretch: "self-stretch",
};

const JUSTIFY_SELF_CLASSES: Record<JustifySelf, string> = {
  auto: "justify-self-auto",
  "flex-start": "justify-self-start",
  "flex-end": "justify-self-end",
  center: "justify-self-center",
  baseline: "justify-self-auto",
  stretch: "justify-self-stretch",
};

const GAP_CLASSES: Record<FlexboxGap, string> = {
  none: "gap-0",
  xs: "gap-1",
  sm: "gap-2",
  md: "gap-4",
  lg: "gap-6",
  xl: "gap-8",
};

/**
 * Tailwind-backed flex container. Keeps the legacy Flexbox API so existing
 * `<Flexbox alignItems=... justifyContent=...>` call-sites continue to work,
 * but emits Tailwind utility classes instead of inline styles.
 */
export const Flexbox = <T extends React.ElementType = "div">({
  alignItems,
  justifyContent,
  flexDirection,
  flexWrap,
  alignContent,
  alignSelf,
  justifySelf,
  align,
  justify,
  gap,
  className,
  as,
  children,
  ...rest
}: FlexboxProps<T>) => {
  const Component = (as ?? "div") as React.ElementType;

  const resolvedAlignItems = alignItems ?? align;
  const resolvedJustifyContent = justifyContent ?? justify;

  return (
    <Component
      className={cn(
        "flex",
        flexDirection ? FLEX_DIRECTION_CLASSES[flexDirection] : null,
        resolvedAlignItems ? ALIGN_ITEMS_CLASSES[resolvedAlignItems] : null,
        resolvedJustifyContent
          ? JUSTIFY_CONTENT_CLASSES[resolvedJustifyContent]
          : null,
        flexWrap ? FLEX_WRAP_CLASSES[flexWrap] : null,
        alignContent ? ALIGN_CONTENT_CLASSES[alignContent] : null,
        alignSelf ? ALIGN_SELF_CLASSES[alignSelf] : null,
        justifySelf ? JUSTIFY_SELF_CLASSES[justifySelf] : null,
        gap ? GAP_CLASSES[gap] : null,
        className,
      )}
      {...(rest as Record<string, unknown>)}
    >
      {children}
    </Component>
  );
};
